import { useAppSelector } from "@/store/app/hooks";
import { DataGroup } from "./DataGroup";
import { cn } from "@nextui-org/theme";
import { ReactComponent as OTRLogo } from "@/assets/img/otr.svg";
import { useMemo } from "react";
import { getPercentageFromValues } from "@/utils/getPercentageFromValues";

export const OTRData = () => {
  const factoringInfo = useAppSelector(
    (state) => state.carrierReport.data?.otrFactoringInfo
  );

  const {
    brokerTestResult,
    dtpLast30,
    dtpLast60,
    dtpChange,
    age0To30,
    age31To60,
    age61To90,
    age91Plus,
    lastFactoredInvoice,
  } = factoringInfo || {};

  const textClassName = cn(" font-medium", {
    "text-success-500": brokerTestResult === "Approved",
    "text-danger-500": brokerTestResult === "Not Approved",
    "text-gray-500": !brokerTestResult,
  });

  const agingPercentages = useMemo(() => {
    return getPercentageFromValues(
      {
        age0To30: age0To30 || 0,
        age31To60: age31To60 || 0,
        age61To90: age61To90 || 0,
        age91Plus: age91Plus || 0,
      },
      true
    );
  }, [age0To30, age31To60, age61To90, age91Plus]);

  const dtpLast30Value = useMemo(() => {
    const last30Value = dtpLast30?.toFixed(2) || "-";
    if (!dtpChange) return last30Value;
    const label = Math.abs(dtpChange);
    const isUp = +dtpChange < 0;
    return (
      <>
        {!!label && (
          <>
            <span
              className={cn("text-xs", {
                "text-green-500": isUp,
                "text-red-500": !isUp,
              })}
            >
              {isUp ? "↑" : "↓"} {label.toFixed(2)}%
            </span>{" "}
          </>
        )}
        {last30Value}
      </>
    );
  }, [dtpChange, dtpLast30]);

  if (!factoringInfo) return null;

  return (
    <DataGroup
      outerClassName="col-span-6 bg-white border border-gray-200"
      innerClassName="grid grid-cols-3 gap-5 gap-y-3"
    >
      <div className="flex justify-between items-center col-span-3">
        <h4 className="text-sm text-gray-950">
          Eligibility for factoring:{" "}
          <span className={textClassName}>{brokerTestResult || "No Data"}</span>
        </h4>
        <a href="https://loadconnect.io/factor-with-otr/">
          <OTRLogo width={80} height={40} />
        </a>
      </div>
      <div className="flex flex-col">
        <h5 className="text-sm text-gray-500">Last Factored Invoice</h5>
        <p className="text-sm text-gray-950">{lastFactoredInvoice}</p>
      </div>
      <div className="flex flex-col">
        <h5 className="text-sm text-gray-500 mb-1">Average Days to Pay</h5>
        <div className="w-full flex justify-between items-center">
          <p className="text-xs text-gray-950">Last 30 days</p>
          <p className="text-sm text-gray-950">{dtpLast30Value || "-"}</p>
        </div>
        <div className="w-full flex justify-between items-center">
          <p className="text-xs text-gray-950">Last 60 days</p>
          <p className="text-sm text-gray-950">
            {dtpLast60?.toFixed(1) || "-"}
          </p>
        </div>
      </div>
      <div className="flex flex-col">
        <h5 className="text-sm text-gray-500 mb-1">Aging report</h5>
        <div className="w-full flex justify-between items-center">
          <p className="text-xs text-gray-950">0-30 days</p>
          <p className="text-sm text-gray-950">
            {agingPercentages.age0To30 || "-"}
          </p>
        </div>
        <div className="w-full flex justify-between items-center">
          <p className="text-xs text-gray-950">31-60 days</p>
          <p className="text-sm text-gray-950">
            {agingPercentages.age31To60 || "-"}
          </p>
        </div>
        <div className="w-full flex justify-between items-center">
          <p className="text-xs text-gray-950">61-90 days</p>
          <p className="text-sm text-gray-950">
            {agingPercentages.age61To90 || "-"}
          </p>
        </div>
        <div className="w-full flex justify-between items-center">
          <p className="text-xs text-gray-950">91+ days</p>
          <p className="text-sm text-gray-950">
            {agingPercentages.age91Plus || "-"}
          </p>
        </div>
      </div>
    </DataGroup>
  );
};
